import { suggestEcuCode } from "../../evaluation/ue/ecuCode";
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import {
  getCourseById,
  updateCourse,
} from "../../../api/cours";
import { getAllUEByPeriod } from "../../../api/ue";
import { getAllEvaluationPeriodByLevel } from "../../../api/generalParam";
import {
  getAllClass,
  getAllClassroom,
  getAllSubject,
} from "../../../api/pedagogicElement";
import { getAllTeachers } from "../../../api/teachers";
import { Select } from "antd";
import Swal from "sweetalert2";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import withPermission from "../../../core/common/autorisations/withPermission";

interface Props {
  show: boolean;
  coursID: string;
  onClose: () => void;
  onSaved: () => void;
  seanceCanEdit?: boolean;
}

interface Subject {
  id: string;
  label: string;
}

interface Teacher {
  id: string;
  UserProfil: {
    firstName: string;
    lastName: string;
  };
}

export interface ClassTable {
  id: string;
  value: string;
  label: string;
  id_level?: string;
}

const EditCoursModal = ({
  show,
  coursID,
  onClose,
  onSaved,
  seanceCanEdit = true,
}: Props) => {
  const { selectedYear } = useAcademicYear();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [allClass, setAllClass] = useState<ClassTable[]>([]);
  const [allClassroom, setAllClassroom] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<ClassTable[]>([]);
  const [classAssignments, setClassAssignments] = useState<Record<string, any>>({});
  const [uesByClass, setUesByClass] = useState<Record<string, any[]>>({});
  const [periodsByClass, setPeriodsByClass] = useState<Record<string, any[]>>({});

  // Schéma de validation du formulaire
  const formSchema = z.object({
    id_subject: z.string().min(1, { message: "La matière est requise" }),
    id_teacherProfil: z.string().min(1, { message: "L'enseignant est requis" }),
    status: z.string().min(1, { message: "L'état est requis" }),
    id_class: z.array(z.string()).min(1, { message: "La classe est requise" }),
    id_classroom: z.string().optional().nullable(),
    seance_number: z
      .number()
      .min(1, { message: "Le nombre de séances est requis" }),
  });

  // Type pour les données du formulaire
  type FormData = z.infer<typeof formSchema>;

  // Initialisation du formulaire
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id_subject: "",
      id_teacherProfil: "",
      status: "",
      id_class: [], // Valeur par défaut
      id_classroom: null,
      seance_number: 1, // Valeur par défaut
    },
  });

  const getModalContainer = () => {
    const modalElement = document.getElementById("edit_cours_modal");
    return modalElement ? modalElement : document.body; // Fallback to document.body if modalElement is null
  };

  // Fonction pour gérer la sélection des classes
  const handleClassChange = (
    selectedIds: string[],
    option?:
      | {
          id: string;
          value: string;
          label: string;
          id_level?: string;
        }
      | {
          id: string;
          value: string;
          label: string;
          id_level?: string;
        }[]
      | undefined,
  ) => {
    // option peut être undefined ou un tableau ou un objet, donc on doit gérer les cas
    let selectedOptions: {
      id: string;
      value: string;
      label: string;
      id_level?: string;
    }[] = [];
    if (Array.isArray(option)) {
      selectedOptions = option as {
        id: string;
        value: string;
        label: string;
        id_level?: string;
      }[];
    } else if (option) {
      selectedOptions = [
        option as {
          id: string;
          value: string;
          label: string;
          id_level?: string;
        },
      ];
    } else {
      // fallback: reconstruire à partir des valeurs sélectionnées
      selectedOptions = allClass.filter((classe) =>
        selectedIds.includes(classe.id),
      );
    }
    setSelectedClass(selectedOptions);
  };
  useEffect(() => {
    setValue(
      "id_class",
      selectedClass.map((classe) => classe.id),
    );
  }, [selectedClass, setValue]);

  useEffect(() => {
    setClassAssignments((current) => {
      const next: Record<string, any> = {};
      selectedClass.forEach((classe) => {
        next[classe.id] = current[classe.id] || { id_class: classe.id, id_evaluationPeriod: null, id_ue: null, codeECU: "", credit: 1, coefficient: 1 };
      });
      return next;
    });
    Promise.all(selectedClass.map(async (classe) => {
      const response = await getAllEvaluationPeriodByLevel(classe.id_level, buildPermissionContext(Resource.COURSE, Action.UPDATE));
      return [classe.id, Array.isArray(response) ? response : response.data || []] as const;
    })).then((entries) => setPeriodsByClass(Object.fromEntries(entries))).catch(() => {
      toast.error("Impossible de charger les semestres des classes sélectionnées");
    });
  }, [selectedClass]);

  const updateAssignment = (classId: string, values: Record<string, any>) => {
    setClassAssignments((current) => ({ ...current, [classId]: { ...current[classId], ...values } }));
  };

  const confirmRemoveFromUe = async (classe: ClassTable) => {
    const result = await Swal.fire({
      title: "Retirer le cours de l’UE ?",
      text: `Le rattachement ECU de ${classe.label} sera supprimé lors de l’enregistrement.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Oui, retirer",
      cancelButtonText: "Annuler",
      confirmButtonColor: "#dc3545",
    });
    if (result.isConfirmed) {
      updateAssignment(classe.id, { id_ue: null, codeECU: "", credit: 1, coefficient: 1 });
    }
  };

  const handlePeriodAssignmentChange = async (classId: string, periodId?: string) => {
    updateAssignment(classId, { id_evaluationPeriod: periodId || null, id_ue: null, codeECU: "" });
    if (!periodId) {
      setUesByClass((current) => ({ ...current, [classId]: [] }));
      return;
    }
    try {
      const response = await getAllUEByPeriod(classId, periodId, buildPermissionContext(Resource.COURSE, Action.UPDATE));
      setUesByClass((current) => ({ ...current, [classId]: response.data || [] }));
    } catch {
      toast.error("Impossible de charger les UE de ce semestre");
    }
  };

  const handleUeAssignmentChange = (classId: string, ueId?: string) => {
    const ue = (uesByClass[classId] || []).find((item) => item.id === ueId);
    updateAssignment(classId, {
      id_ue: ueId || null,
      codeECU: ue ? suggestEcuCode(ue.codeUE, ue.SubjectUEs) : "",
    });
  };

  const fetchCoursDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      // TODO: Implémenter la fonction de récupération des détails du cours
      if (!coursID) return;
      const response = await getCourseById(
        coursID,
        buildPermissionContext(Resource.COURSE, Action.UPDATE),
      );
      const coursData = response.data;
      // console.log("cours: ", coursData);
      const classCourse = allClass.filter((cls) =>
        coursData?.course?.Classes.some((c: any) => c.id === cls.id),
      );
      setSelectedClass(classCourse);

      // Mise à jour des valeurs du formulaire
      setValue("id_subject", coursData.course.id_subject);
      setValue("id_teacherProfil", coursData.course.id_teacherProfil);
      setValue("status", coursData.course.state);
      setValue(
        "id_class",
        classCourse.map((cls) => cls.id),
      );
      setValue("id_classroom", coursData.course.id_classroom);
      setValue("seance_number", coursData.course.number_session);
      const assignments = (coursData.course.SubjectUEs || []).reduce((acc: Record<string, any>, ecu: any) => {
        const classId = ecu.UE?.id_class;
        if (classId) acc[classId] = { id_class: classId, id_evaluationPeriod: ecu.UE?.id_evaluationPeriod || null, id_ue: ecu.id_ue, codeECU: ecu.codeECU, credit: ecu.credit, coefficient: ecu.coefficient };
        return acc;
      }, {});
      classCourse.forEach((classe) => {
        assignments[classe.id] ||= { id_class: classe.id, id_evaluationPeriod: null, id_ue: null, codeECU: "", credit: 1, coefficient: 1 };
      });
      setClassAssignments(assignments);
      const ueEntries = await Promise.all(Object.values(assignments).filter((item: any) => item.id_evaluationPeriod).map(async (item: any) => {
        const ueResponse = await getAllUEByPeriod(item.id_class, item.id_evaluationPeriod, buildPermissionContext(Resource.COURSE, Action.UPDATE));
        return [item.id_class, ueResponse.data || []] as const;
      }));
      setUesByClass(Object.fromEntries(ueEntries));
    } catch (error: any) {
      // console.error(
      //   "Erreur lors de la récupération des détails du cours:",
      //   error,
      // );
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produite lors de la récupération des détails du cours",
      );
    } finally {
      setIsLoading(false);
    }
  }, [allClass, coursID, setValue]);
  // Récupération des données du cours à modifier
  useEffect(() => {
    if (!show) return;
    fetchCoursDetails();
  }, [show, fetchCoursDetails]);

  // Fonction pour récupérer les matières
  const fetchSubjects = useCallback(async () => {
    try {
      if (!selectedYear?.id) return;
      const subjectsData = await getAllSubject(
        selectedYear.id,
        buildPermissionContext(Resource.COURSE, Action.UPDATE),
      );
      const formattedSubjects = subjectsData.data.map((subject: any) => ({
        id: subject.id,
        label: subject.label,
      }));
      setSubjects(formattedSubjects);
    } catch (error: any) {
      // console.error("Erreur lors de la récupération des matières:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  }, [selectedYear?.id]);

  // Fonction pour récupérer les enseignants
  const fetchTeachers = useCallback(async () => {
    try {
      const teachersData = await getAllTeachers(
        buildPermissionContext(Resource.COURSE, Action.UPDATE),
      );
      const formattedTeachers = teachersData.data.map((teacher: any) => ({
        id: teacher.id,
        UserProfil: {
          firstName: teacher.firstname,
          lastName: teacher.lastname,
        },
      }));
      setTeachers(formattedTeachers);
    } catch (error: any) {
      // console.log("erreur récupération des enseignants ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  }, []);

  // fonction pour récupérer les classes
  const fetchAllClass = useCallback(async () => {
    try {
      const response = await getAllClass(
        selectedYear?.id,
        buildPermissionContext(Resource.COURSE, Action.UPDATE),
      );
      // console.log("Classes response: ", response);
      const formattedClass = response.data
        ?.slice() // pour éviter de modifier directement la réponse
        .sort((a: any, b: any) => {
          // Tri par Level.label
          const levelCompare = a.Level.label.localeCompare(
            b.Level.label,
            "fr",
            { numeric: true },
          );
          if (levelCompare !== 0) return levelCompare;

          // Si même Level, tri par class label
          return a.label.localeCompare(b.label, "fr", { numeric: true });
        })
        .map((item: any) => ({
          id: item.id,
          value: item.id,
          label: `${item.Level.label} - ${item.label} `,
          id_level: item?.Level?.id,
        }));
      setAllClass(formattedClass || []);
    } catch (error: any) {
      // console.log("erreur de chargement des classes : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  }, [selectedYear?.id]);

  // fonction pour recuperer toutes les salles de classe de lannée académique sélectionnée
  const fetchAllClassroom = useCallback(async () => {
    try {
      const response = await getAllClassroom(
        selectedYear?.id,
        buildPermissionContext(Resource.COURSE, Action.UPDATE),
      );
      const formattedClassroom = response.data.map((classroom: any) => ({
        id: classroom.id,
        value: classroom.id,
        label: classroom.label,
      }));
      // console.log("Classroom: ", formattedClassroom);
      setAllClassroom(formattedClassroom);
    } catch (error: any) {
      console.log("erreur de chargement des salles : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  }, [selectedYear?.id]);
  // Récupération des matières et des enseignants, classes
  useEffect(() => {
    if (show && selectedYear?.id) {
      fetchTeachers();
      fetchSubjects();
      fetchAllClass();
      fetchAllClassroom();
    }
  }, [show, selectedYear?.id, fetchTeachers, fetchSubjects, fetchAllClass, fetchAllClassroom]);

  // Soumission du formulaire
  const onSubmit = async (data: FormData) => {
    if (!selectedYear?.id) {
      toast.error("Veuillez sélectionner une année académique");
      return;
    }

    const assignments = data.id_class.map((id) => classAssignments[id]);
    if (assignments.some((item) => item?.id_ue && (!item.codeECU?.trim() || Number(item.credit) <= 0 || Number(item.coefficient) < 0))) {
      toast.error("Renseignez le code ECU, les crédits et le coefficient pour chaque UE sélectionnée");
      return;
    }
    try {
      setIsLoading(true);

      //  on modifie le cours
      await updateCourse(
        coursID,
        {
          id_subject: data.id_subject,
          id_teacherProfil: data.id_teacherProfil,
          state: data.status,
          id_classroom: data.id_classroom || null,
          number_session: data.seance_number,
          class_assignments: assignments,
        },
        buildPermissionContext(Resource.COURSE, Action.UPDATE),
      );
      toast.success("Cours modifié avec succès");
      onSaved(); // Rafraîchir la liste des cours
      onClose(); // Fermer le modal
    } catch (error: any) {
      // console.error("Erreur lors de la modification du cours:", error);
      await Swal.fire({
        title: "Modification impossible",
        text: error.response?.data?.message || error.response?.data?.error || "Une erreur s'est produite lors de la modification du cours",
        icon: "error",
        confirmButtonText: "Compris",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Réinitialisation du formulaire à la fermeture du modal
  const handleClose = () => {
    reset();
    onClose();
  };

  // useEffect(() => {
  //   console.log("Erreurs: ", errors);
  // }, [errors]);
  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="xl"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Modifier un cours</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center">
            <div className="spinner-border" role="status"></div>
            <span className="visually-hidden">Chargement...</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row" id="edit_cours_modal">
              {/* Matière */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Matière <span className="text-danger">*</span>
                </label>
                <Controller
                  control={control}
                  name="id_subject"
                  render={({ field }) => (
                    <Select
                      showSearch
                      allowClear
                      className={
                        errors.id_subject ? "select is-invalid" : "select"
                      }
                      options={subjects.map((subject) => ({
                        value: subject.id,
                        label: subject.label,
                      }))}
                      placeholder="Sélectionner une matière"
                      getPopupContainer={getModalContainer}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      style={{ width: "100%" }}
                      {...field}
                      value={field.value || undefined}
                    />
                  )}
                />
                {errors.id_subject && (
                  <div className="invalid-feedback">
                    {errors.id_subject.message}
                  </div>
                )}
              </div>

              {/* Enseignant */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Enseignant <span className="text-danger">*</span>
                </label>
                <Controller
                  control={control}
                  name="id_teacherProfil"
                  render={({ field }) => (
                    <Select
                      showSearch
                      allowClear
                      className={
                        errors.id_teacherProfil ? "select is-invalid" : "select"
                      }
                      options={teachers.map((teacher) => ({
                        value: teacher.id,
                        label: `${teacher.UserProfil.firstName} ${teacher.UserProfil.lastName}`,
                      }))}
                      placeholder="Sélectionner un enseignant"
                      getPopupContainer={getModalContainer}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      style={{ width: "100%" }}
                      {...field}
                      value={field.value || undefined}
                    />
                  )}
                />
                {errors.id_teacherProfil && (
                  <div className="invalid-feedback">
                    {errors.id_teacherProfil.message}
                  </div>
                )}
              </div>

              {/* État */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  État <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${errors.status ? "is-invalid" : ""}`}
                  {...register("status")}
                  value={watch("status")}
                >
                  <option value="Approuvé">Approuvé</option>
                  <option value="Non Approuvé">Non approuvé</option>
                </select>
                {errors.status && (
                  <div className="invalid-feedback">
                    {errors.status.message}
                  </div>
                )}
              </div>
              {/* Nombre de séances */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Nombre de séances <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className={`form-control ${
                    errors.seance_number ? "is-invalid" : ""
                  }`}
                  disabled={!seanceCanEdit}
                  {...register("seance_number", {
                    valueAsNumber: true,
                  })}
                />
                {errors.seance_number && (
                  <div className="invalid-feedback">
                    {errors.seance_number.message}
                  </div>
                )}
              </div>

              {/* Classe */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Classe(s) <span className="text-danger">*</span>
                </label>

                <Select
                  id="class-selector"
                  mode="multiple"
                  allowClear
                  className="select"
                  getPopupContainer={getModalContainer}
                  style={{
                    width: "100%",
                  }}
                  placeholder="Seletionner une classe"
                  defaultValue={[]}
                  value={selectedClass.map((classe) => classe.id)}
                  options={allClass}
                  onChange={handleClassChange}
                  // Permet de chercher par nom/prenom a lieu de l'id
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
                {errors.id_class && (
                  <div className="invalid-feedback">
                    {errors.id_class.message}
                  </div>
                )}
              </div>

              {/* Salle */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Salle</label>

                <Select
                  id="classroom-selector"
                  allowClear
                  className="select"
                  getPopupContainer={getModalContainer}
                  style={{
                    width: "100%",
                  }}
                  placeholder="Seletionner une salle"
                  defaultValue={null}
                  value={watch("id_classroom") || null}
                  // options={[]}
                  options={allClassroom.map((classroom) => ({
                    value: classroom.id,
                    label: classroom.label,
                  }))}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(value) => setValue("id_classroom", value)}
                />
                {errors.id_classroom && (
                  <div className="invalid-feedback">
                    {errors.id_classroom.message}
                  </div>
                )}
              </div>
              {selectedClass.length > 0 && (
                <div className="col-12 mt-2">
                  <div className="border rounded-3 p-3 bg-light">
                    <h6 className="mb-1">Rattachement aux unités d’enseignement</h6>
                    <p className="text-muted small mb-3">Les informations ECU sont propres à chaque classe.</p>
                    {selectedClass.map((classe) => {
                      const assignment = classAssignments[classe.id] || {};
                      return (
                        <div className="bg-white border rounded-2 p-3 mb-2" key={classe.id}>
                          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                            <div className="fw-semibold">{classe.label}</div>
                            {assignment.id_ue && (
                              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => confirmRemoveFromUe(classe)}>
                                <i className="ti ti-unlink me-1" /> Retirer ce cours de l’UE
                              </button>
                            )}
                          </div>
                          <div className="d-grid gap-2 overflow-auto" style={{ gridTemplateColumns: "repeat(3, minmax(170px, 1fr)) repeat(2, minmax(90px, .55fr))" }}>
                            <div><label className="form-label small mb-1">Semestre</label><Select allowClear showSearch style={{ width: "100%" }} placeholder="Semestre" value={assignment.id_evaluationPeriod || undefined}
                              options={(periodsByClass[classe.id] || []).map((period) => ({ value: period.id, label: period.label }))}
                              onChange={(value) => handlePeriodAssignmentChange(classe.id, value)} getPopupContainer={() => document.body} dropdownStyle={{ zIndex: 2000 }} /></div>
                            <div><label className="form-label small mb-1">UE</label><Select allowClear showSearch style={{ width: "100%" }} placeholder="Sans UE" disabled={!assignment.id_evaluationPeriod} value={assignment.id_ue || undefined}
                              options={(uesByClass[classe.id] || []).map((ue) => ({ value: ue.id, label: `${ue.codeUE} - ${ue.label}` }))}
                              onChange={(value) => handleUeAssignmentChange(classe.id, value)} getPopupContainer={() => document.body} dropdownStyle={{ zIndex: 2000 }}
                              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} /></div>
                            <div><label className="form-label small mb-1">Code ECU</label><input className="form-control" placeholder="Ex. 1 MGT2440" disabled={!assignment.id_ue} value={assignment.codeECU || ""} onChange={(e) => updateAssignment(classe.id, { codeECU: e.target.value })} /></div>
                            <div><label className="form-label small mb-1">Créd.</label><input className="form-control" type="number" min="1" placeholder="Créd." disabled={!assignment.id_ue} value={assignment.credit ?? 1} onChange={(e) => updateAssignment(classe.id, { credit: Number(e.target.value) })} /></div>
                            <div><label className="form-label small mb-1">Coeff.</label><input className="form-control" type="number" min="0" placeholder="Coeff." disabled={!assignment.id_ue} value={assignment.coefficient ?? 1} onChange={(e) => updateAssignment(classe.id, { coefficient: Number(e.target.value) })} /></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end mt-3">
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={handleClose}
                disabled={isLoading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Enregistrement...
                  </>
                ) : (
                  "Enregistrer"
                )}
              </button>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default withPermission(
  EditCoursModal,
  [buildPermission(Resource.COURSE, Action.UPDATE)],
  "masquer",
);
